import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type Resolver = (download: boolean) => void;

interface OpenPayload {
  filename: string;
  resolve: Resolver;
}

const EVENT_NAME = "thiia:confirm-raw-export:open";

export interface ConfirmRawExportRequest {
  filename: string;
}

/**
 * Asks the user whether to download the raw, unformatted document after
 * they cancelled the cloud template flow.
 * Resolves with `true` to download, `false` to abort.
 */
export function openConfirmRawExport(
  req: ConfirmRawExportRequest,
): Promise<boolean> {
  return new Promise((resolve) => {
    window.dispatchEvent(
      new CustomEvent<OpenPayload>(EVENT_NAME, {
        detail: { filename: req.filename, resolve },
      }),
    );
  });
}

export default function ConfirmRawExportHost() {
  const [state, setState] = useState<OpenPayload | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<OpenPayload>).detail;
      setState(detail);
    };
    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
  }, []);

  const resolve = (download: boolean) => {
    state?.resolve(download);
    setState(null);
  };

  return (
    <AlertDialog open={!!state} onOpenChange={(o) => { if (!o) resolve(false); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Export sans mise en forme&nbsp;?</AlertDialogTitle>
          <AlertDialogDescription>
            Vous avez annulé le choix du modèle. Voulez-vous tout de même
            télécharger le document brut <strong>{state?.filename}</strong>,
            ou abandonner l'export&nbsp;?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => resolve(false)}>
            Abandonner
          </AlertDialogCancel>
          <AlertDialogAction onClick={() => resolve(true)}>
            Télécharger sans modèle
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
